import type { FC } from 'react';
import type { FileIconProps } from 'react-file-icon';
import type { Field } from './UploadFile';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileIcon as OriginalFileIcon, defaultStyles } from 'react-file-icon';
import { ContentCopy, Delete, Download, Launch, MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { STORE_KEYS } from 'types/store';
import { useStore } from 'contexts';
import { Box, CircularProgress, IconButton, Menu, MenuItem, styled, Tooltip, Typography } from '@mui/material';
import { IMAGE_EXTENSIONS } from 'components/user/Records/components/FileUploadsItem/FileItem';
import { FileWrapper, ImageContainer, TextContainer } from 'components/user/Records/components/FileUploadsItem/FileItem.styles';
import { useParams } from 'react-router-dom';
import { PreviewItem } from 'components/user/Records/components/FileUploadsItem/PreviewItem';
import { getBaseUrl } from 'utils/baseUrl';
import { getDisplayFileName, getDownloadFileName, getHeliosFileExtensionAndUrl, normalizeFileType } from 'utils/file/fileUtils';
import { isKnownIconExtension } from 'utils/file/fileIconUtils';
import { downloadHeliosFile } from 'utils/file/downloadFile';
import { useFilePreview } from './useFilePreview';
import { useSpamGuard } from 'utils/hooks';

const Image = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

interface FileItemProps {
  file: Field;
  disabledRemove?: boolean;
  onRemove?: () => void;
}

const FileIcon = OriginalFileIcon as FC<FileIconProps>;

export const FileItem: FC<FileItemProps> = ({ file, disabledRemove, onRemove }) => {
  const { t } = useTranslation();
  const { agendaId = '' } = useParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const { getItem } = useStore();
  const auth = getItem(STORE_KEYS.AUTH);
  const { isPending, execute } = useSpamGuard(isImageLoading);

  const { extension, url } = useMemo(() => getHeliosFileExtensionAndUrl(file, agendaId), [file, agendaId]);
  const normalizedExtension = normalizeFileType(extension);
  const iconStyles = useMemo(() => (isKnownIconExtension(normalizedExtension) ? defaultStyles[normalizedExtension] : {}), [normalizedExtension]);
  const isImage = IMAGE_EXTENSIONS.includes(normalizedExtension);
  const displayName = getDisplayFileName(file.name);

  const { previewState, handleClosePreview, handleOpenPreview } = useFilePreview({ file, url, extension, auth });

  useEffect(() => {
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      if (!auth?.jwtToken || !isImage) {
        setIsImageLoading(false);
        return;
      }

      setIsImageLoading(true);

      try {
        const response = await fetch(url, {
          headers: {
            Authorization: auth.jwtToken,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch image');

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      } catch {
        setImageSrc(null);
      } finally {
        setIsImageLoading(false);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [auth?.jwtToken, isImage, url]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      await downloadHeliosFile({ file, url, extension, token: auth?.jwtToken ?? '' });
    } catch {
      toast.error(t('user.detail.file.loadFailed'));
    }
  }, [file, url, extension, auth?.jwtToken, t]);

  const fullName = getDownloadFileName(displayName, extension);

  const handleCopy = useCallback(async () => {
    const copyUrl = getBaseUrl(`/user/download/${encodeURIComponent(url)}?fileName=${encodeURIComponent(fullName)}`);
    await navigator.clipboard.writeText(copyUrl);
    toast.success(t('common.copied'));
  }, [url, fullName, t]);

  const menuItems = [
    {
      icon: <Delete sx={{ mr: 1 }} />,
      label: 'common.delete',
      action: onRemove,
      show: !disabledRemove,
    },
    {
      icon: <Download sx={{ mr: 1 }} />,
      label: 'common.download',
      action: () => execute(handleDownload),
      show: true,
    },
    {
      icon: <Launch sx={{ mr: 1 }} />,
      label: 'common.view',
      action: () => execute(handleOpenPreview),
      show: true,
    },
    {
      icon: <ContentCopy sx={{ mr: 1 }} />,
      label: 'user.detail.file.copyLink',
      action: handleCopy,
      show: true,
    },
  ];

  return (
    <FileWrapper elevation={1} onDoubleClick={() => execute(handleOpenPreview)}>
      <ImageContainer>
        {isPending ? (
          <CircularProgress size={32} />
        ) : isImage && imageSrc ? (
          <Image src={imageSrc} alt={file?.name ?? 'file_img'} />
        ) : (
          <Box width="48px" height="48px">
            <FileIcon extension={normalizedExtension} {...iconStyles} />
          </Box>
        )}
      </ImageContainer>

      <TextContainer>
        <Box overflow="hidden">
          <Tooltip title={displayName} placement="top">
            <Typography noWrap variant="body2" fontWeight="500" sx={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
              {displayName}
            </Typography>
          </Tooltip>
        </Box>

        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVert />
        </IconButton>
      </TextContainer>

      <Menu disableScrollLock anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {menuItems.map(
          ({ icon, label, action, show }) =>
            show && (
              <MenuItem
                key={label}
                onClick={() => {
                  action?.();
                  handleMenuClose();
                }}>
                {icon}
                <Typography variant="body2">{t(label)}</Typography>
              </MenuItem>
            )
        )}
      </Menu>

      <PreviewItem
        open={previewState.open}
        onClose={handleClosePreview}
        url={previewState.url ?? ''}
        name={displayName}
        fileType={normalizedExtension}
      />
    </FileWrapper>
  );
};
